import { clientConfig } from "@/lib/config/client-config";
import * as Sentry from "@sentry/nextjs";

/**
 * Session Replay, added after the visitor allows analytics rather than at init.
 *
 * Error monitoring itself stays on unconditionally: it keeps the service working
 * and carries no advertising purpose. Replay records what the visitor did on
 * screen, which is analytics, so it waits for consent. The sample rates stay in
 * `Sentry.init` because the integration reads them from the client options.
 */
export function enableSentryReplay(): void {
  if (clientConfig.sentryEnv !== "production") return;
  if (Sentry.getReplay()) return;

  Sentry.addIntegration(
    Sentry.replayIntegration({
      // Privacy: [data-private] masks sensitive text (addresses, balances, PnL); [data-private-block]
      // blocks elements whose ATTRIBUTES leak (title/aria-label/href) — attribute masking only runs
      // under maskAllText.
      // Inputs are always masked, media always blocked.
      maskAllText: false,
      mask: ["[data-private]"],
      block: ["[data-private-block]"],
      maskAllInputs: true,
      blockAllMedia: true,
      // Request detail (URL/method/status only elsewhere) is captured solely for our own API;
      // the only extra header is `correlationId` — the name our api-client sends — to join a
      // replay with backend logs. No response headers: the API sets none we could read.
      // No bodies: the default (true) would record API payloads (balances, addresses, auth).
      networkDetailAllowUrls: [window.location.origin, clientConfig.apiUrl],
      networkCaptureBodies: false,
      networkRequestHeaders: ["correlationId"],
    }),
  );
}

/**
 * Stops recording on a withdrawal. The installed SDK's `stop()` takes no
 * options, so the reload that follows a withdrawal is what actually guarantees
 * nothing keeps recording; this just ends it as soon as the choice is made.
 */
export function disableSentryReplay(): void {
  void Sentry.getReplay()?.stop();
}
